/**
 * WalkthroughRecorderBanner
 * ─────────────────────────
 * Global admin banner for the iter313d recorder tool. Always visible
 * for admins in the top-right of every page. Three roles:
 *
 *   1. Show whether recording is active + the current focus tag
 *   2. Let the operator set / change / clear the focus tag inline
 *   3. Surface real-time alarms (rules R1/R3/R4/R5 fired by the
 *      backend polling loop) as a red badge with click-through detail
 *
 * Design notes:
 *   · No modal — inline popover keeps it lightweight
 *   · Polls /health + /focus + /alarms every 15 s while mounted
 *   · Recovers gracefully if backend restarts (focus is DB-persisted)
 *   · Non-admins never see anything (component returns null)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, Circle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { visibleInterval } from '@/lib/visibleInterval';

const POLL_MS = 15000;

export default function WalkthroughRecorderBanner() {
  const { user, api } = useAuth() || {};
  const [focus, setFocus] = useState(null);       // string | null
  const [paused, setPaused] = useState(false);
  const [health, setHealth] = useState(null);     // {alarm_poller, last_poll_at}
  const [alarms, setAlarms] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftTag, setDraftTag] = useState('');

  const isAdmin = user?.role === 'admin';

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [f, h, a] = await Promise.all([
        api.get('/walkthrough/focus'),
        api.get('/walkthrough/health'),
        api.get('/walkthrough/alarms?since_minutes=60'),
      ]);
      setFocus(f?.data?.tag || null);
      setPaused(!!f?.data?.paused);
      setHealth(h?.data || null);
      setAlarms(a?.data?.alarms || []);
    } catch (e) { /* non-fatal */ }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    refresh();
    return visibleInterval(refresh, POLL_MS);
  }, [isAdmin, refresh]);

  const setFocusTag = async (tag) => {
    try {
      const r = await api.post('/walkthrough/focus', { tag });
      setFocus(r?.data?.tag || null);
      setPaused(!!r?.data?.paused);
      setEditing(false);
      setDraftTag('');
    } catch (e) { /* non-fatal */ }
  };

  const togglePause = async () => {
    try {
      const r = await api.post('/walkthrough/pause', { paused: !paused });
      setPaused(!!r?.data?.paused);
    } catch (e) { /* non-fatal */ }
  };

  if (!isAdmin) return null;

  const state = paused
    ? 'paused'
    : alarms.length > 0
      ? 'alarm'
      : (health?.alarm_poller === 'healthy' ? 'active' : 'stale');
  const dotColour = state === 'alarm' ? 'text-red-500' : state === 'active' ? 'text-emerald-500' : state === 'paused' ? 'text-slate-500' : 'text-slate-400';
  const badgeBg   = state === 'alarm'
    ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
    : state === 'stale'
      ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
      : state === 'paused'
        ? 'bg-slate-200 border-slate-400 text-slate-700 hover:bg-slate-300'
      : focus
        ? 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
        : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200';
  const label = state === 'alarm'
    ? `${alarms.length} ALARM${alarms.length > 1 ? 'S' : ''}`
    : state === 'paused'
      ? `⏸ Paused${focus ? ` · ${focus}` : ''}`
      : focus
        ? `● Rec · ${focus}`
        : 'Recorder · set focus';

  return (
    <div className="relative">
      <button
        data-testid="walkthrough-recorder-badge"
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 border-2 rounded-full text-xs font-bold tracking-wide shadow-sm transition-all ${badgeBg}`}
        title={state === 'alarm' ? 'Click to view active alarms' : `Recorder ${state}. Focus: ${focus || '(none — click to set)'}`}
      >
        {state === 'alarm'
          ? <AlertTriangle size={14} strokeWidth={2.5} />
          : <Circle size={9} strokeWidth={0} className={`fill-current ${dotColour}`} />}
        <span>{label}</span>
      </button>

      {open && (
        <div data-testid="walkthrough-recorder-panel" className="absolute top-full right-0 mt-2 w-80 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-slate-700 text-xs font-semibold">
              <Activity size={12} />
              Walkthrough Recorder
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="walkthrough-pause-btn"
                onClick={togglePause}
                className={`text-[11px] font-bold px-2 py-0.5 rounded border transition-colors ${
                  paused
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700'
                    : 'bg-red-600 hover:bg-red-700 text-white border-red-700'
                }`}
                title={paused ? 'Resume capture' : 'Pause capture (stop recording clicks)'}
              >
                {paused ? '▶ Resume' : '■ Stop'}
              </button>
              <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${state === 'alarm' ? 'bg-red-100 text-red-700' : state === 'active' ? 'bg-emerald-100 text-emerald-700' : state === 'paused' ? 'bg-slate-300 text-slate-700' : 'bg-slate-100 text-slate-500'}`} data-testid="walkthrough-recorder-state">
                {state}
              </span>
            </div>
          </div>

          {/* Focus tag */}
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1 flex items-center justify-between">
              <span>Focus tag (scenario name)</span>
              {!focus && !editing && <span className="text-emerald-700 normal-case tracking-normal">← start here</span>}
            </div>
            {editing ? (
              <div className="flex items-center gap-1.5">
                <input
                  data-testid="walkthrough-focus-input"
                  autoFocus
                  value={draftTag}
                  onChange={e => setDraftTag(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') setFocusTag(draftTag.trim()); if (e.key === 'Escape') { setEditing(false); setDraftTag(''); }}}
                  className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded"
                  placeholder="WT-1, gutter-race, painting-push, etc."
                />
                <button data-testid="walkthrough-focus-save-btn" onClick={() => setFocusTag(draftTag.trim())} className="px-2 py-1 text-xs bg-slate-800 text-white rounded hover:bg-slate-900">Set</button>
              </div>
            ) : focus ? (
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-emerald-800" data-testid="walkthrough-focus-current">● {focus}</span>
                <div className="flex items-center gap-2">
                  <button data-testid="walkthrough-focus-edit-btn" onClick={() => { setEditing(true); setDraftTag(focus); }} className="text-[11px] text-slate-500 hover:text-slate-800 underline">Change</button>
                  <button data-testid="walkthrough-focus-clear-btn" onClick={() => setFocusTag('')} className="text-[11px] text-slate-500 hover:text-red-600 underline">Clear</button>
                </div>
              </div>
            ) : (
              <button data-testid="walkthrough-focus-edit-btn" onClick={() => { setEditing(true); setDraftTag(''); }} className="w-full text-left text-xs text-emerald-700 hover:text-emerald-900 px-2 py-1.5 border border-dashed border-emerald-300 rounded hover:bg-emerald-50">
                + Set a tag (e.g. <span className="font-mono font-semibold">WT-1</span>) to group this session's clicks
              </button>
            )}
          </div>

          {/* Alarms */}
          <div className="max-h-72 overflow-y-auto">
            <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-slate-400 flex items-center justify-between">
              <span>Recent alarms (last 60 min)</span>
              <span data-testid="walkthrough-alarms-count">{alarms.length}</span>
            </div>
            {alarms.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400" data-testid="walkthrough-alarms-empty">No alarms — clean run.</div>
            ) : alarms.map(a => (
              <div key={a.id} data-testid={`walkthrough-alarm-${a.rule}`} className="px-3 py-2 border-t border-slate-100">
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-[10px] uppercase font-bold ${a.severity === 'HIGH' ? 'text-red-700' : 'text-amber-700'}`}>
                    {a.rule} · {a.severity}
                  </span>
                  <span className="text-[10px] text-slate-400">{new Date(a.ts).toLocaleTimeString()}</span>
                </div>
                <div className="text-xs font-medium text-slate-800">{a.name}</div>
                <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{a.why}</div>
                {a.appraisal_id && <div className="text-[10px] text-slate-400 mt-1">appraisal: {a.appraisal_id.slice(-8)}</div>}
              </div>
            ))}
          </div>

          <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-400 flex items-center justify-between">
            <span>Poller: {health?.alarm_poller || '—'}</span>
            <span>{health?.last_poll_at ? `updated ${new Date(health.last_poll_at).toLocaleTimeString()}` : ''}</span>
          </div>
        </div>
      )}
    </div>
  );
}
